import React, { useState, useEffect, useCallback } from 'react';
import { phasesAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Milestone, CheckCircle2, Circle, Clock, Pause } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: Circle, color: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
  on_hold: { label: 'On Hold', icon: Pause, color: 'bg-amber-100 text-amber-700' },
};

export default function ProjectPhases({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptyForm = { name: '', description: '', status: 'not_started', start_date: '', end_date: '', billing_percentage: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchPhases = useCallback(async () => {
    try {
      setLoading(true);
      const res = await phasesAPI.list(projectId);
      setPhases(res.data);
    } catch (err) {
      console.error('Failed to fetch phases:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchPhases(); }, [fetchPhases]);

  const handleCreateDefaults = async () => {
    setFormLoading(true);
    try {
      const res = await phasesAPI.createDefaults(projectId);
      setPhases(res.data);
    } catch (err) {
      console.error('Failed to create defaults:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { ...form, billing_percentage: parseFloat(form.billing_percentage) || 0 };
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      if (editing) {
        const res = await phasesAPI.update(editing.phase_id, payload);
        setPhases(phases.map(p => p.phase_id === editing.phase_id ? res.data : p));
      } else {
        const res = await phasesAPI.create(projectId, payload);
        setPhases([...phases, res.data]);
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    } catch (err) {
      console.error('Failed to save phase:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusChange = async (phaseId, newStatus) => {
    try {
      const res = await phasesAPI.update(phaseId, { status: newStatus });
      setPhases(phases.map(p => p.phase_id === phaseId ? res.data : p));
    } catch (err) {
      console.error('Failed to update phase:', err);
    }
  };

  const handleDelete = async (phaseId) => {
    try {
      await phasesAPI.delete(phaseId);
      setPhases(phases.filter(p => p.phase_id !== phaseId));
    } catch (err) {
      console.error('Failed to delete phase:', err);
    }
  };

  const openEdit = (phase) => {
    setEditing(phase);
    setForm({
      name: phase.name, description: phase.description || '',
      status: phase.status, start_date: phase.start_date ? phase.start_date.split('T')[0] : '',
      end_date: phase.end_date ? phase.end_date.split('T')[0] : '',
      billing_percentage: phase.billing_percentage || '',
    });
    setDialogOpen(true);
  };

  const completedCount = phases.filter(p => p.status === 'completed').length;
  const totalBilling = phases.reduce((s, p) => s + (p.billing_percentage || 0), 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

  if (loading) return <div className="animate-pulse"><div className="h-48 bg-muted rounded" /></div>;

  return (
    <div className="space-y-4" data-testid="project-phases">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Project Phases</h3>
          <p className="text-xs text-muted-foreground">{completedCount}/{phases.length} completed{totalBilling > 0 ? ` — ${totalBilling}% billing allocated` : ''}</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {phases.length === 0 && (
              <Button variant="outline" onClick={handleCreateDefaults} disabled={formLoading} data-testid="create-default-phases-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Milestone className="h-4 w-4 mr-2" />}Use Template
              </Button>
            )}
            <Button onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="add-phase-btn">
              <Plus className="h-4 w-4 mr-2" />Add Phase
            </Button>
          </div>
        )}
      </div>

      {phases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Milestone className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-muted-foreground">No phases defined yet</p>
            <p className="text-xs text-muted-foreground mt-1">Use the template to get started with standard interior design phases</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {phases.map((phase, idx) => {
            const config = STATUS_CONFIG[phase.status] || STATUS_CONFIG.not_started;
            const Icon = config.icon;
            return (
              <Card key={phase.phase_id} data-testid={`phase-${phase.phase_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Step number */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${phase.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {idx + 1}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm">{phase.name}</h4>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${config.color}`}>
                          {config.label}
                        </span>
                        {phase.billing_percentage > 0 && (
                          <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 bg-muted rounded">{phase.billing_percentage}% billing</span>
                        )}
                      </div>
                      {(phase.start_date || phase.end_date) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatDate(phase.start_date)}{phase.start_date && phase.end_date ? ' — ' : ''}{formatDate(phase.end_date)}
                        </p>
                      )}
                      {phase.description && <p className="text-xs text-muted-foreground mt-1">{phase.description}</p>}
                    </div>
                    {/* Actions */}
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Select value={phase.status} onValueChange={v => handleStatusChange(phase.phase_id, v)}>
                          <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(phase)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(phase.phase_id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Phase' : 'Add Phase'}</DialogTitle>
            <DialogDescription>Define a project milestone or phase</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Phase Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Design Development" data-testid="phase-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional details..." />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Billing %</Label>
                  <Input type="number" step="0.1" min="0" max="100" value={form.billing_percentage} onChange={e => setForm({ ...form, billing_percentage: e.target.value })} placeholder="e.g., 30" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-phase-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Add Phase'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
